import axios from 'axios';
import { getAccessToken, parseJwt } from './auth';
import { useAuthStore } from './store';

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
  }
}

export const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, v));
        } else {
          searchParams.append(key, String(value));
        }
      }
      return searchParams.toString();
    }
  }
});

api.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  const schoolId = localStorage.getItem('active_school_id');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Respect an explicitly set school (e.g. offline outbox sync sends the school stored with each entry).
  if (schoolId && !config.headers['X-School-Id']) {
    config.headers['X-School-Id'] = schoolId;
  }
  // The API narrows the caller's roles to the role they are currently acting in (e.g. coach viewing as student).
  const activeRole = useAuthStore.getState().activeRole;
  if (activeRole && !config.headers['X-Active-Role']) {
    config.headers['X-Active-Role'] = activeRole;
  }
  return config;
});

let _redirecting = false;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Network errors (no `response`: offline / server unreachable) are never treated as 401 — no login redirect.
    if (error.response?.status === 401 && !_redirecting && !error.config?.skipAuthRedirect) {
      _redirecting = true;
      try {
        const storeState = useAuthStore.getState();
        if (storeState.impersonationToken && storeState.originalToken) {
          const { userManager } = await import('./auth');
          let adminToken = storeState.originalToken;
          try {
            let user = await userManager.getUser();
            if (user && user.expired) {
              user = await userManager.signinSilent();
            }
            if (user?.access_token) {
              adminToken = user.access_token;
            }
          } catch {
            // Ignore silent refresh error, proceed with originalToken
          }

          try {
            const activeSchoolId = localStorage.getItem('active_school_id') || '';
            const tokenPayload = parseJwt(storeState.impersonationToken);
            const targetUserId = tokenPayload?.sub;
            if (targetUserId && activeSchoolId) {
              const res = await api.post(
                `/schools/${activeSchoolId}/auth/impersonate?targetUserId=${targetUserId}`,
                {},
                {
                  headers: {
                    Authorization: `Bearer ${adminToken}`,
                    // Re-exchange runs with the admin's token, so act in the admin's role, not the impersonated one.
                    'X-Active-Role': storeState.originalActiveRole ?? 'Admin',
                  },
                  skipAuthRedirect: true,
                }
              );

              if (res.data?.accessToken) {
                storeState.startImpersonation(
                  res.data.accessToken,
                  res.data.refreshToken || '',
                  adminToken,
                  storeState.originalUserContext!,
                  storeState.originalActiveRole,
                  storeState.originalPath
                );

                _redirecting = false;
                const config = error.config;
                config.headers.Authorization = `Bearer ${res.data.accessToken}`;
                return api.request(config);
              }
            }
          } catch (ex) {
            // Re-exchange failed — clear impersonation and fall through
            storeState.stopImpersonation();
          }
        }

        const { userManager, isBrowserOffline, isAuthNetworkFailure } = await import('./auth');
        // Try silent refresh first for normal flow
        try {
          const user = await userManager.signinSilent();
          if (user) {
            _redirecting = false;
            // Retry the original request with the new token
            const config = error.config;
            config.headers.Authorization = `Bearer ${user.access_token}`;
            return api.request(config);
          }
        } catch (renewError) {
          // KeyCloak unreachable (offline / network failure): keep the user and the offline cache, no redirect.
          if (isAuthNetworkFailure(renewError)) return Promise.reject(error);
          // Silent refresh rejected, fall through to redirect
        }
        // The login page cannot load without a connection — keep the stored user until we are back online.
        if (isBrowserOffline()) return Promise.reject(error);
        // If we get here, we truly need to re-authenticate
        await userManager.removeUser();
        localStorage.setItem('auth_return_url', window.location.pathname + window.location.search);
        const { login } = await import('./auth');
        await login();
      } finally {
        _redirecting = false;
      }
    }
    return Promise.reject(error);
  }
);
